import _defineProperty from "/home/runner/work/experian-public-site-tng/experian-public-site-tng/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { clocksNow, combine, elapsed, ExperimentalFeature, generateUUID, isExperimentalFeatureEnabled, sanitize, toServerDuration } from '@datadog/browser-core';
import { RumEventType, VitalType } from '../../rawRumEvent.types';
export function createCustomVitalsState() {
  var vitalsByName = new Map();
  var vitalsByReference = new WeakMap();
  return {
    vitalsByName: vitalsByName,
    vitalsByReference: vitalsByReference
  };
}
export function startVitalCollection(lifeCycle, pageStateHistory, customVitalsState) {
  function isValid(vital) {
    return !pageStateHistory.wasInPageStateDuringPeriod("frozen" /* PageState.FROZEN */, vital.startClocks.relative, vital.duration);
  }
  function addDurationVital(vital) {
    if (isValid(vital)) {
      lifeCycle.notify(12 /* LifeCycleEventType.RAW_RUM_EVENT_COLLECTED */, processVital(vital));
    }
  }
  function addOperationStepVital(name, stepType, options, failureReason) {
    if (!isExperimentalFeatureEnabled(ExperimentalFeature.FEATURE_OPERATION_VITAL)) {
      return;
    }
    var _ref = options || {},
      operationKey = _ref.operationKey,
      context = _ref.context,
      description = _ref.description,
      handlingStack = _ref.handlingStack;
    var vital = {
      name: name,
      type: VitalType.OPERATION_STEP,
      operationKey: operationKey,
      failureReason: failureReason,
      stepType: stepType,
      startClocks: clocksNow(),
      context: sanitize(context),
      description: description,
      handlingStack: handlingStack
    };
    lifeCycle.notify(12 /* LifeCycleEventType.RAW_RUM_EVENT_COLLECTED */, processVital(vital));
  }
  return {
    addOperationStepVital: addOperationStepVital,
    addDurationVital: addDurationVital,
    startDurationVital: function startDurationVital(name) {
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var ref = _startDurationVital(customVitalsState, name, options);
      var vitalState = customVitalsState.vitalsByReference.get(ref);
      if (vitalState) {
        lifeCycle.notify(16 /* LifeCycleEventType.VITAL_STARTED */, vitalState);
      }
      return ref;
    },
    stopDurationVital: function stopDurationVital(nameOrRef) {
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      _stopDurationVital(addDurationVital, customVitalsState, nameOrRef, options);
    }
  };
}
function _startDurationVital(_ref2, name) {
  var vitalsByName = _ref2.vitalsByName,
    vitalsByReference = _ref2.vitalsByReference;
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var vital = _objectSpread({
    id: generateUUID(),
    name: name,
    startClocks: clocksNow()
  }, options);
  // To avoid leaking implementation details of the vital, we return a reference to it.
  var reference = {
    __dd_vital_reference: true
  };
  vitalsByName.set(name, vital);
  // To avoid memory leaks caused by the creation of numerous references (e.g., from improper useEffect implementations), we use a WeakMap.
  vitalsByReference.set(reference, vital);
  return reference;
}
export { _startDurationVital as startDurationVital };
function _stopDurationVital(stopCallback, _ref3, nameOrRef) {
  var vitalsByName = _ref3.vitalsByName,
    vitalsByReference = _ref3.vitalsByReference;
  var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  var vitalStart = typeof nameOrRef === 'string' ? vitalsByName.get(nameOrRef) : vitalsByReference.get(nameOrRef);
  if (!vitalStart) {
    return;
  }
  stopCallback(buildDurationVital(vitalStart, vitalStart.startClocks, options, clocksNow()));
  if (typeof nameOrRef === 'string') {
    vitalsByName["delete"](nameOrRef);
  } else {
    vitalsByReference["delete"](nameOrRef);
  }
}
export { _stopDurationVital as stopDurationVital };
function buildDurationVital(vitalStart, startClocks, stopOptions, stopClocks) {
  var _a;
  return {
    id: vitalStart.id,
    name: vitalStart.name,
    type: VitalType.DURATION,
    startClocks: startClocks,
    duration: elapsed(startClocks.timeStamp, stopClocks.timeStamp),
    context: combine(vitalStart.context, stopOptions.context),
    description: (_a = stopOptions.description) !== null && _a !== void 0 ? _a : vitalStart.description,
    handlingStack: vitalStart.handlingStack
  };
}
function processVital(vital) {
  var startClocks = vital.startClocks,
    type = vital.type,
    name = vital.name,
    description = vital.description,
    context = vital.context,
    handlingStack = vital.handlingStack;
  var vitalId = vital.type === VitalType.DURATION ? vital.id : undefined;
  var vitalData = _objectSpread({
    id: vitalId !== null && vitalId !== void 0 ? vitalId : generateUUID(),
    type: type,
    name: name,
    description: description
  }, type === VitalType.DURATION ? {
    duration: toServerDuration(vital.duration)
  } : {
    step_type: vital.stepType,
    operation_key: vital.operationKey,
    failure_reason: vital.failureReason
  });
  return {
    rawRumEvent: {
      date: startClocks.timeStamp,
      vital: vitalData,
      type: RumEventType.VITAL,
      context: context
    },
    startClocks: startClocks,
    duration: type === VitalType.DURATION ? vital.duration : undefined,
    domainContext: handlingStack ? {
      handlingStack: handlingStack
    } : {}
  };
}